<script setup>
import { computed } from 'vue'
import TeamMemberCell from './TeamMemberCell.vue'
import TeamRoleCell from './TeamRoleCell.vue'
import TeamAccessCell from './TeamAccessCell.vue'
import TeamProjectsCell from './TeamProjectsCell.vue'
import TeamStatusCell from './TeamStatusCell.vue'
import TeamRowActions from './TeamRowActions.vue'
import { teamRoleKey } from '@/Utils/teamAccessSummary'

const props = defineProps({
	member: { type: Object, required: true },
	permissionTogglestatus: { type: Boolean, default: false },
	canMutate: { type: Boolean, default: true },
	activeProject: { type: Object, default: null },
	showProjects: { type: Boolean, default: false },
})

const emit = defineEmits(['edit', 'add', 'resend', 'toggle', 'remove'])

const isOwner = computed(() => teamRoleKey(props.member.role) === 'owner')
const isPending = computed(() => props.member.status === 'pending')
const notHere = computed(() => props.member.onProject === false)
const isPaused = computed(() => !isPending.value && !notHere.value && props.member.enabled === false)
const dim = computed(() => isPaused.value || notHere.value)
</script>

<template>
	<article class="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-3/50">
		<TeamMemberCell :member="member" :dim="dim" class="flex-1 min-w-0">
			<!-- No role badge for someone not on this workspace: "Not added" below and
			the chips already say it, and "None" next to a name misreads. -->
			<template v-if="!notHere" #badge>
				<TeamRoleCell :member="member" class="shrink-0" />
			</template>
			<template #meta>
				<div class="mt-1 flex flex-wrap items-center gap-x-2 text-tiny text-surface-7">
					<!-- Status word stays full opacity even when paused, so "Enable" reads. -->
					<TeamStatusCell :member="member" dot />
					<template v-if="!isPending && !notHere">
						<span aria-hidden="true" class="text-surface-6">·</span>
						<TeamAccessCell :member="member" compact :class="isPaused ? 'opacity-60' : ''" />
					</template>
				</div>
				<TeamProjectsCell
					v-if="showProjects && !isOwner"
					:member="member"
					:active-project-id="activeProject?.id || ''"
					compact
					class="mt-1.5"
				/>
			</template>
		</TeamMemberCell>

		<!-- Right: kebab. Reserve the slot on the owner row so identities align. -->
		<div class="shrink-0">
			<TeamRowActions
				v-if="!isOwner"
				:member="member"
				:permission-togglestatus="permissionTogglestatus"
				:can-mutate="canMutate"
				:active-project="activeProject"
				:show-projects="showProjects"
				@edit="emit('edit', $event)"
				@add="emit('add', $event)"
				@resend="emit('resend', $event)"
				@toggle="emit('toggle', $event)"
				@remove="emit('remove', $event)"
			/>
			<div v-else class="size-8" aria-hidden="true" />
		</div>
	</article>
</template>
